import FeedbackItemRegenerating from 'components/Grading/FeedbackItemRegenerating';
import FeedbackRevertButton from 'components/Grading/FeedbackRevertButton';
import FeedbackRevertPreview from 'components/Grading/FeedbackRevertPreview';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, RichTextArea, Stack, styled, Tag, Text } from 'shell/components';
import { COMMENTARY_ITEM_ID, getFeedbackRevertPreview } from 'utils/feedbackRegeneration';
import { stripHTML } from 'utils/helper';

const LegacyFeedbackRenderer = ({
    currentSubmission,
    form,
    setForm,
    setShowFailedDialog,
    needsFeedbackRegeneration = false,
    feedbackUpdatedItemIds = [],
    pendingFeedbackItemIds = [],
    onRevertFeedback,
}) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [isEditing, setIsEditing] = useState(false);
    const [revertPreview, setRevertPreview] = useState(null);
    const [value, setValue] = useState(form.feedback || '');
    const isPending = pendingFeedbackItemIds.includes(COMMENTARY_ITEM_ID);
    const isFeedbackUpdated = feedbackUpdatedItemIds.includes(COMMENTARY_ITEM_ID);
    const availableRevertPreview = getFeedbackRevertPreview(currentSubmission.feedback_history, COMMENTARY_ITEM_ID);
    const canRevert = Boolean(availableRevertPreview);

    const handleClickEdit = () => {
        setIsEditing(true);
        setValue(form.feedback || '');
    };

    const handleClickCopy = async () => {
        await navigator.clipboard.writeText(stripHTML(value));
        message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
    };

    const handleClickDelete = () => {
        setDialog({
            open: true,
            message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
            description: t(
                'dialog.delete-feedback.description',
                'Are you sure you want to delete this feedback? This can’t be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({ ...current, feedback: '' }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickSave = () => {
        setForm((current) => ({ ...current, feedback: value }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(form.feedback);
    };

    const handleOpenRevertPreview = () => setRevertPreview(availableRevertPreview);

    const handleConfirmRevertPreview = async () => {
        if (!revertPreview || typeof onRevertFeedback !== 'function') return false;

        const didRevert = await onRevertFeedback({
            historyId: revertPreview.historyId,
            itemIds: [COMMENTARY_ITEM_ID],
        });
        if (didRevert) setRevertPreview(null);
        return didRevert;
    };

    useEffect(() => {
        setIsEditing(false);
        setRevertPreview(null);
        setValue(form.feedback || '');
    }, [currentSubmission.id, form.feedback]);

    return (
        <>
            <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p1'} weight={'semibold'}>
                    {t('feedback-section.feedback-label', 'Feedback')}
                </Text>
                <Stack direction={'row'} spacing={2}>
                    <IconButton
                        aria-label={!form.feedback ? 'add feedback' : 'edit feedback'}
                        disabled={isPending || Boolean(revertPreview)}
                        kind={!form.feedback ? 'add' : 'edit'}
                        size={'small'}
                        tooltip={{
                            component: !form.feedback
                                ? t('common:controls.add', 'Add')
                                : t('common:controls.edit', 'Edit'),
                        }}
                        variant={'square'}
                        onClick={handleClickEdit}
                    />
                    <IconButton
                        aria-label={'copy feedback'}
                        disabled={!form.feedback || isPending || Boolean(revertPreview)}
                        kind={'copy'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.copy', 'Copy') }}
                        variant={'square'}
                        onClick={handleClickCopy}
                    />
                    <IconButton
                        aria-label={'delete feedback'}
                        disabled={!form.feedback || isPending || Boolean(revertPreview)}
                        kind={'delete'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.delete', 'Delete') }}
                        variant={'square'}
                        onClick={handleClickDelete}
                    />
                </Stack>
            </BackgroundStack>
            <Stack sx={{ py: 4, px: 5 }} spacing={4}>
                {(needsFeedbackRegeneration ||
                    isFeedbackUpdated ||
                    (canRevert && typeof onRevertFeedback === 'function')) && (
                    <Stack
                        sx={{ width: '100%' }}
                        alignItems={'center'}
                        direction={'row'}
                        justifyContent={'space-between'}
                    >
                        {!isPending &&
                            (needsFeedbackRegeneration ? (
                                <Tag type={'general'} value={'regenerationSuggested'} />
                            ) : (
                                isFeedbackUpdated && <Tag type={'general'} value={'feedbackUpdated'} />
                            ))}
                        <FeedbackRevertButton
                            canRevert={canRevert}
                            itemIds={[COMMENTARY_ITEM_ID]}
                            pending={isPending || isEditing || Boolean(revertPreview)}
                            onRevertFeedback={handleOpenRevertPreview}
                        />
                    </Stack>
                )}
                {revertPreview && (
                    <FeedbackRevertPreview
                        feedback={revertPreview.feedback}
                        feedbackType={'legacy'}
                        generatedOn={revertPreview.generatedOn}
                        onCancel={() => setRevertPreview(null)}
                        onRevert={handleConfirmRevertPreview}
                    />
                )}
                {currentSubmission.is_failed && (
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            {t(
                                'feedback-section.feedback-generation-failed',
                                'We were unable to generate feedback for this assignment. You can try to regenerate, or provide feedback manually.'
                            )}
                        </Text>
                        {setShowFailedDialog && (
                            <Button variant={'borderless'} onClick={() => setShowFailedDialog(true)}>
                                {t('common:ui.learn-more', 'Learn more')}
                            </Button>
                        )}
                    </Stack>
                )}
                {isPending ? (
                    <FeedbackItemRegenerating sx={{ minHeight: '30vh' }} />
                ) : !isEditing ? (
                    <>
                        {!currentSubmission.is_failed && !form.feedback ? (
                            <Text sx={{ transform: 'translateY(30vh)' }} align={'center'} color={'light'}>
                                {t('feedback-section.feedback-deleted', 'This feedback was deleted')}
                            </Text>
                        ) : (
                            <Text sx={{ pb: 8 }} rich>
                                {form.feedback}
                            </Text>
                        )}
                    </>
                ) : (
                    <Stack spacing={3}>
                        <CustomRichTextArea
                            isFailed={currentSubmission.is_failed}
                            placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                            value={value}
                            onChange={setValue}
                        />
                        <Stack direction={'row-reverse'} spacing={3}>
                            <Button onClick={handleClickSave}>{t('common:controls.save', 'Save')}</Button>
                            <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                                {t('common:controls.cancel', 'Cancel')}
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomRichTextArea = styled(RichTextArea)(({ isFailed }) => {
    return {
        height: isFailed ? 'calc(100vh - 373px)' : 'calc(100vh - 269px)',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

export default LegacyFeedbackRenderer;
